import { projectUniversalCompletionOverview } from "@excellent/core/verification-universal-console";
import { SWRConfig } from "swr";
import { useApi } from "@excellent/ui/api";
import { classifyWorkSubject } from "@excellent/ui/api/contracts/verification-work-chain";

import { verificationCanonicalClients } from "../../api/canonical-clients";
import { useCanonicalEnvironmentOptional } from "../../canonical-environment-provider";
import { href } from "../../nav";
import { ActionButton, Mono, Section, ToneBadge } from "../../ui";
import { PageError, PageFrame, type VerificationPageProps } from "../page-frame";
import { UniversalProtocolConsole } from "./universal-protocol-console";
import {
  canonicalWorkHref,
  canonicalWorkReadQuery,
  canonicalWorkResponseMatches,
} from "./environment-read-scope";

/** Same short labels as the receipts ledger and Work console, so a class
 * reads identically everywhere it appears. */
const SUBJECT_CLASS_SHORT_LABEL = {
  "real-subject": "Real subject",
  "self-test-fixture": "Self-test",
  "synthetic-fixture": "Synthetic",
} as const;

/**
 * The universal completion-aware console is the sole Work authority surface.
 * Legacy workflow/run/finding joins remain available on their own historical
 * routes, but must not be presented here as a competing current ledger.
 */
export function WorkLedgerPage({ onNavigate }: VerificationPageProps) {
  const canonicalContext = useCanonicalEnvironmentOptional();
  const workQuery = canonicalWorkReadQuery(canonicalContext);
  const environmentReady = Boolean(workQuery);
  const cacheKey = canonicalContext?.cacheKey ?? "environment-unresolved";

  return (
    <PageFrame
      page="work"
      title="Work ledger"
      lede="Current completion-aware Work, contracts, attempts, receipts, reviews, and consequences."
      wide
    >
      <SWRConfig key={cacheKey} value={{ provider: () => new Map() }}>
        <WorkLedgerContent
          canonicalContext={canonicalContext}
          workQuery={workQuery}
          localEnvironmentReady={environmentReady}
          onNavigate={onNavigate}
        />
      </SWRConfig>
    </PageFrame>
  );
}

function WorkLedgerContent({
  localEnvironmentReady,
  canonicalContext,
  workQuery,
  onNavigate,
}: VerificationPageProps & {
  localEnvironmentReady: boolean;
  canonicalContext: ReturnType<typeof useCanonicalEnvironmentOptional>;
  workQuery: ReturnType<typeof canonicalWorkReadQuery>;
}) {
  const overview = useApi(
    verificationCanonicalClients.workLedger.operation,
    workQuery ? { query: workQuery } : null,
  );
  const responseCurrent = canonicalWorkResponseMatches(overview.data, workQuery);
  const currentWork =
    responseCurrent && overview.data
      ? projectUniversalCompletionOverview(overview.data).current.work
      : [];

  if (overview.error || (overview.data && !responseCurrent)) {
    return (
      <PageError
        title="Universal Work ledger unavailable"
        error={overview.error ?? "The Work response did not match the selected Environment."}
      />
    );
  }

  if (!localEnvironmentReady) {
    return (
      <PageError
        title="Canonical Environment context required"
        error="A verified canonical Environment is required before the Work ledger can be read."
      />
    );
  }

  return (
    <>
      <UniversalProtocolConsole />

      {currentWork.length > 0 ? (
        <Section
          title="Work details"
          note="Open the retained detail route for a current completion-aware Work record."
        >
          <div className="flex flex-col gap-2" data-current-work-detail-links="">
            {currentWork.map((row) => (
              <div className="flex min-w-0 items-center justify-between gap-3" key={row.id}>
                {/* The title is the row's identity; the id stays one disclosure away. */}
                <div className="flex min-w-0 flex-col gap-1">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate font-medium">{row.title}</span>
                    {(() => {
                      const subject = classifyWorkSubject(row.source.uri);
                      return (
                        <ToneBadge tone={subject.class === "real-subject" ? "accent" : "unknown"}>
                          {SUBJECT_CLASS_SHORT_LABEL[subject.class]}
                        </ToneBadge>
                      );
                    })()}
                  </div>
                  <details className="type-subtext text-muted-foreground">
                    <summary className="cursor-pointer">Reference ID</summary>
                    <div className="mt-1 break-all">
                      <Mono>{row.id}</Mono>
                    </div>
                  </details>
                </div>
                <ActionButton
                  actionId="work.open-detail"
                  onClick={() =>
                    onNavigate(canonicalWorkHref(href("work-ledger", row.id), canonicalContext))
                  }
                >
                  Detail
                </ActionButton>
              </div>
            ))}
          </div>
        </Section>
      ) : null}
    </>
  );
}
